/**
 * The one candle tape every chart picture draws: the Panel Hub's Chart and
 * History Chart cards and the New Layout preview's chart cells.
 *
 * Traced from Chris's chart sketch (2026-09-27, `chart-sketch-clean.svg`, 36
 * bars): a gap-up run, a flush back to the open, a bounce to a lower high, a
 * double bottom, then a grind to the high of day. Prices are normalized so the
 * day's low is 0 and its high is 1. Only the candles are kept; the sketch's
 * grid, trendline, labels and colours are not (colours are theme tokens).
 *
 * A handful of bars were edited from the trace so the tape reads less drawn
 * and more traded; each one is marked `edited:` below.
 */

/** [open, high, low, close], 0 = the day's low, 1 = the day's high. */
export type Ohlc = readonly [number, number, number, number]

export const CHART_TAPE: readonly Ohlc[] = [
  [0.005, 0.129, 0.0, 0.124],
  [0.124, 0.234, 0.114, 0.223],
  [0.223, 0.376, 0.211, 0.364],
  // edited: a marubozu, no wicks, mid-run.
  [0.364, 0.53, 0.364, 0.526],
  [0.526, 0.686, 0.509, 0.669],
  // edited: one red pause inside the opening run.
  [0.669, 0.702, 0.622, 0.641],
  [0.641, 0.783, 0.632, 0.768],
  // edited: a long upper wick, the rejection at the first high.
  [0.768, 0.842, 0.685, 0.697],
  [0.697, 0.707, 0.509, 0.517],
  [0.517, 0.524, 0.279, 0.286],
  [0.286, 0.301, 0.142, 0.157],
  [0.157, 0.211, 0.068, 0.193],
  [0.193, 0.384, 0.192, 0.383],
  [0.383, 0.572, 0.383, 0.571],
  // edited: a long upper wick, the rejection at the lower high.
  [0.571, 0.648, 0.563, 0.587],
  [0.587, 0.602, 0.473, 0.491],
  [0.491, 0.508, 0.356, 0.374],
  [0.374, 0.386, 0.248, 0.276],
  // edited: a longer lower wick, the flush into the first bottom.
  [0.276, 0.279, 0.188, 0.225],
  [0.225, 0.38, 0.215, 0.369],
  [0.369, 0.464, 0.363, 0.449],
  [0.449, 0.46, 0.349, 0.361],
  [0.361, 0.382, 0.281, 0.316],
  // edited: the second bottom undercuts a touch, then holds.
  [0.316, 0.32, 0.205, 0.228],
  [0.228, 0.273, 0.218, 0.258],
  [0.258, 0.31, 0.246, 0.297],
  [0.297, 0.352, 0.281, 0.336],
  [0.336, 0.415, 0.32, 0.398],
  // edited: a small red candle breaks up the closing grind...
  [0.398, 0.452, 0.379, 0.386],
  [0.386, 0.581, 0.381, 0.568],
  [0.568, 0.684, 0.562, 0.678],
  [0.678, 0.714, 0.653, 0.709],
  // edited: ...and a second one before the last push.
  [0.709, 0.738, 0.688, 0.697],
  [0.697, 0.889, 0.692, 0.884],
  [0.884, 0.969, 0.869, 0.955],
  [0.955, 1.0, 0.951, 0.996],
]

/** A laid-out candle: [centre x, body top, body bottom, wick high, wick low, rising?]. */
export type LaidCandle = [number, number, number, number, number, boolean]

export interface TapeRect {
  x: number
  y: number
  w: number
  h: number
}

/**
 * Merge `bars` into `n` buckets the way a longer timeframe would: open of the
 * first, close of the last, the highest high and the lowest low. Any `n`
 * works; bucket i takes bars [floor(i*len/n), floor((i+1)*len/n)).
 */
export function aggregateBars(bars: readonly Ohlc[], n: number): Ohlc[] {
  const count = Math.max(1, Math.min(bars.length, Math.floor(n)))
  const out: Ohlc[] = []
  for (let i = 0; i < count; i++) {
    const from = Math.floor((i * bars.length) / count)
    const to = Math.floor(((i + 1) * bars.length) / count)
    const slice = bars.slice(from, to)
    out.push([
      slice[0][0],
      Math.max(...slice.map((b) => b[1])),
      Math.min(...slice.map((b) => b[2])),
      slice[slice.length - 1][3],
    ])
  }
  return out
}

/**
 * Lay bars into a rect: one pitch per bar across the width, the bars' own
 * high-to-low range stretched to the height. Returns the candles and a moving
 * average of closes (every bar, `period` bars back) in the same space.
 */
export function layTape(
  bars: readonly Ohlc[],
  rect: TapeRect,
  period = 4,
): { candles: LaidCandle[]; average: [number, number][]; pitch: number } {
  const pitch = rect.w / bars.length
  const hi = Math.max(...bars.map((b) => b[1]))
  const lo = Math.min(...bars.map((b) => b[2]))
  const range = hi - lo || 1
  const y = (price: number) => rect.y + ((hi - price) / range) * rect.h

  const candles: LaidCandle[] = bars.map(([o, h, l, c], i) => [
    rect.x + pitch * i + pitch / 2,
    y(Math.max(o, c)),
    y(Math.min(o, c)),
    y(h),
    y(l),
    c >= o,
  ])
  const average: [number, number][] = bars.map((_, i) => {
    const window = bars.slice(Math.max(0, i - period + 1), i + 1)
    const mean = window.reduce((sum, b) => sum + b[3], 0) / window.length
    return [candles[i][0], y(mean)]
  })
  return { candles, average, pitch }
}

/** An SVG path through points, straight segments. */
export function linePath(points: readonly [number, number][]): string {
  return points
    .map(([x, y], i) => `${i === 0 ? 'M' : 'L'}${x.toFixed(1)} ${y.toFixed(1)}`)
    .join(' ')
}

/**
 * The hub cards' fixed 160 x 100 picture: every bar of the tape inside the
 * cards' usual 12-unit margins. Merging to fewer, fatter bars was tried and
 * lost the shape; all 36 still read at card size.
 */
export const HUB_TAPE = layTape(CHART_TAPE, { x: 12, y: 14, w: 136, h: 72 })

/** Centre x of the History card's anchored day, a shade past the middle. */
export const ANCHOR_X = HUB_TAPE.candles[Math.floor(HUB_TAPE.candles.length * 0.6)][0]
